import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'

/**
 * Figma (node 112:601):
 * Container: bg-background, border border-border, rounded-xl (14px), max-w-[425px], overflow-clip
 * Header: p-4, flex flex-col gap-2
 * Title: text-base (16px) font-medium (500) leading-none text-foreground
 * Description: text-sm font-normal leading-5 text-muted-foreground
 * Content: px-4 pb-4
 * Footer: p-4, border-t, bg-muted/50, flex gap-2 items-center justify-end
 * Close: absolute right-[15px] top-[15px], size-4, opacity-70
 */
export type DialogSize = 'sm' | 'default' | 'lg' | 'full'

const sizeClasses: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  default: 'max-w-[425px]',
  lg: 'max-w-2xl',
  full: 'max-w-4xl',
}

export interface IntuiDialogProps {
  /** Dialog title */
  title: string
  /** Dialog description (below title) */
  description?: string
  /** Dialog body content */
  children: ReactNode
  /** Footer content (buttons, actions) */
  footer?: ReactNode
  /** Trigger element — if not provided, use `open`/`onOpenChange` controlled mode */
  trigger?: ReactNode
  /** Controlled open state */
  open?: boolean
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void
  /** Dialog size */
  size?: DialogSize
  /** Hide the default close (X) button */
  hideClose?: boolean
  /** Additional className for the content */
  className?: string
}

function IntuiDialog({
  title,
  description,
  children,
  footer,
  trigger,
  open,
  onOpenChange,
  size = 'default',
  hideClose = false,
  className,
}: IntuiDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        hideDefaultClose={hideClose}
        className={cn('rounded-xl p-0 gap-0 overflow-clip', sizeClasses[size], className)}
      >
        {/* Figma: header p-4 gap-2 */}
        <DialogHeader className="p-4 gap-2">
          <DialogTitle className="text-base font-medium leading-none">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-sm leading-5">{description}</DialogDescription>
          )}
        </DialogHeader>
        {/* Figma: content px-4 pb-4 */}
        <div className="px-4 pb-4">{children}</div>
        {/* Figma: footer p-4 border-t bg-muted/50 justify-end gap-2 */}
        {footer && <DialogFooter className="p-4 border-t bg-muted/50">{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  )
}

export { IntuiDialog, DialogClose as IntuiDialogClose }
